import { useCallback, useEffect, useMemo, useState } from "react"

import { useTranslation } from "react-i18next"

import { Column, DataTable } from "components/tables/DataTable"

import { cmsSeoService } from "services/cms-seo/cms-seo.service"
import { seoService } from "services/seo/seo.service"
import { SeoOverrideGroup, SportsbookSport } from "services/seo/type"

import { useBrandSite } from "../BrandSiteContext"
import { AsyncList, FilterField, IndexFlag, InfoBox, inputClass, LocaleChips, PathText, primaryButtonClass } from "../CmsUi"
import { RouteSeoModal, RouteSeoState } from "../RouteSeoModal"

/**
 * Where this brand mounts the sportsbook. The panel serves every brand and the
 * backend has no flag for it, so it is stated here rather than guessed: a
 * casino-only brand has no sport pages at all, and listing 76 of them as if it
 * did is worse than listing none.
 */
type Mount = "casino-first" | "sportsbook-first" | "none"

const MOUNT_STORAGE_KEY = "cms-seo.sportsbook-mount"

const PREFIX_BY_MOUNT: Record<Exclude<Mount, "none">, string> = {
  "casino-first": "/sportsbook",
  "sportsbook-first": ""
}

const readStoredMount = (): Mount => {
  try {
    const stored = window.localStorage.getItem(MOUNT_STORAGE_KEY)
    return stored === "sportsbook-first" || stored === "none" ? stored : "casino-first"
  } catch {
    return "casino-first"
  }
}

interface SportRow extends SportsbookSport {
  path: string
  override: SeoOverrideGroup | null
}

/**
 * Every sport the sportsbook routes, with the SEO each one currently has.
 *
 * The paths carry the provider's entity id — Soccer is `soccer-1` — which an
 * editor has no way to know, so they are listed rather than typed.
 */
export function SportsbookSeoTab() {
  const { t } = useTranslation("translation", { useSuspense: false })
  const site = useBrandSite()

  const [sports, setSports] = useState<SportsbookSport[]>([])
  const [groups, setGroups] = useState<SeoOverrideGroup[]>([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState("")
  const [onlyConfigured, setOnlyConfigured] = useState(false)
  const [mount, setMount] = useState<Mount>(readStoredMount)
  const [formState, setFormState] = useState<RouteSeoState>({ kind: "none" })

  const load = useCallback(async () => {
    setLoading(true)
    const [catalogue, overrides] = await Promise.all([cmsSeoService.listSportsbookSports(), seoService.listOverrideGroups()])
    setSports(catalogue)
    setGroups(overrides ?? [])
    setLoading(false)
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const chooseMount = (next: Mount) => {
    setMount(next)
    try {
      window.localStorage.setItem(MOUNT_STORAGE_KEY, next)
    } catch {
      // A browser refusing storage costs the editor one extra click, nothing more.
    }
  }

  const rows = useMemo<SportRow[]>(() => {
    if (mount === "none") return []

    const prefix = PREFIX_BY_MOUNT[mount]
    const byPath = new Map(groups.map((group) => [group.path, group]))

    return sports.map((sport) => {
      const path = `${prefix}/${sport.segment}`
      return { ...sport, path, override: byPath.get(path) ?? null }
    })
  }, [sports, groups, mount])

  const needle = search.trim().toLowerCase()
  const visible = rows.filter((row) => {
    if (onlyConfigured && !row.override) return false
    if (!needle) return true
    return row.name.toLowerCase().includes(needle) || row.path.toLowerCase().includes(needle)
  })

  const configuredCount = rows.filter((row) => row.override).length

  const columns: Column<SportRow>[] = [
    {
      header: t("cmsSeo.col.sport", { defaultValue: "Sport" }),
      render: (row) => <span className="text-sm font-medium">{row.name}</span>
    },
    {
      header: t("cmsSeo.col.path", { defaultValue: "Path" }),
      render: (row) =>
        site.origin ? (
          <a href={site.url(site.defaultLocale, row.path)} target="_blank" rel="noreferrer" onClick={(event) => event.stopPropagation()}>
            <PathText className="underline decoration-dotted underline-offset-2">{row.path}</PathText>
          </a>
        ) : (
          <PathText>{row.path}</PathText>
        )
    },
    {
      header: t("cmsSeo.col.title", { defaultValue: "Title" }),
      wrap: true,
      render: (row) => (
        <span className="text-sm">
          {row.override?.title || (
            <span className="opacity-50">{t("cmsSeo.sportsDefaultTitle", { defaultValue: "{{name}} (automatic)", name: row.name })}</span>
          )}
        </span>
      )
    },
    {
      header: t("cmsSeo.col.index", { defaultValue: "Index" }),
      hideBelow: "md",
      // A flag reports a decision someone made. Without a row there is none to report.
      render: (row) =>
        row.override ? (
          <IndexFlag indexed={row.override.indexed} />
        ) : (
          <span className="text-xs opacity-50">{t("cmsSeo.sportsNoOverride", { defaultValue: "Default" })}</span>
        )
    },
    {
      header: t("cmsSeo.col.locales", { defaultValue: "Locales" }),
      hideBelow: "lg",
      render: (row) => <LocaleChips locales={row.override?.locales ?? []} missing={[]} />
    },
    {
      header: t("common.actions", { defaultValue: "Actions" }),
      align: "right",
      render: (row) => (
        <button
          type="button"
          className={primaryButtonClass}
          onClick={(event) => {
            event.stopPropagation()
            setFormState({ kind: "edit", path: row.path })
          }}
        >
          {row.override
            ? (t("common.edit", { defaultValue: "Edit" }) as string)
            : (t("cmsSeo.addSeo", { defaultValue: "Add SEO" }) as string)}
        </button>
      )
    }
  ]

  const mountOptions: { value: Mount; label: string }[] = [
    { value: "casino-first", label: t("cmsSeo.sportsPrefixCasino", { defaultValue: "/sportsbook/…" }) as string },
    {
      value: "sportsbook-first",
      label: t("cmsSeo.sportsPrefixSportsbook", { defaultValue: "/… (sportsbook-first brand)" }) as string
    },
    { value: "none", label: t("cmsSeo.sportsPrefixNone", { defaultValue: "This brand has no sportsbook" }) as string }
  ]

  return (
    <div className="flex flex-col gap-5 p-4 sm:p-5">
      <div className="flex flex-wrap items-end gap-3">
        <FilterField label={t("common.search", { defaultValue: "Search" }) as string} grow>
          <input
            className={inputClass}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t("cmsSeo.searchSports", { defaultValue: "Search by sport or path…" }) as string}
          />
        </FilterField>

        <FilterField label={t("cmsSeo.sportsPrefix", { defaultValue: "Sportsbook lives at" }) as string}>
          <select className={inputClass} value={mount} onChange={(event) => chooseMount(event.target.value as Mount)}>
            {mountOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </FilterField>

        {mount !== "none" && (
          <label className="flex min-w-[150px] cursor-pointer items-center gap-2 pb-2.5 text-sm">
            <input
              type="checkbox"
              className="checkbox checkbox-sm"
              checked={onlyConfigured}
              onChange={(event) => setOnlyConfigured(event.target.checked)}
            />
            {t("cmsSeo.sportsOnlyConfigured", { defaultValue: "Only sports with SEO" })}
          </label>
        )}
      </div>

      {mount === "none" ? (
        <InfoBox>
          {t("cmsSeo.sportsDisabled", {
            defaultValue:
              "This brand does not run a sportsbook, so none of these pages exist and nothing here needs configuring. Change the setting above if that is wrong."
          })}
        </InfoBox>
      ) : (
        <>
          <InfoBox>
            {t("cmsSeo.sportsInfo", {
              defaultValue:
                "Every sport the sportsbook routes. Each page already has a working title and H1 taken from the sport's own name — add a row here only where you want different copy."
            })}
            <div className="mt-2">
              {t("cmsSeo.sportsIdInfo", {
                defaultValue:
                  "The number in the path is the provider's id for that sport and is part of the URL — pick the sport here rather than typing the path, or the override will never match."
              })}
            </div>
            <div className="mt-2">
              {t("cmsSeo.sportsDepthInfo", {
                defaultValue:
                  "League and event pages below a sport are deliberately not indexed, so they need nothing here. To cover them in bulk, use an SEO Rule."
              })}
            </div>
            <div className="mt-2">
              {t("cmsSeo.sportsMountInfo", {
                defaultValue:
                  "The list assumes the mount point set above. A sport only exists — and is only indexed — if this brand actually serves the sportsbook there; open a path to check before writing copy for it."
              })}
            </div>
          </InfoBox>

          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t("cmsSeo.sportsCount", {
              defaultValue: "{{configured}} of {{total}} sports have their own SEO.",
              configured: configuredCount,
              total: rows.length
            })}
          </p>

          <AsyncList
            loading={loading}
            isEmpty={visible.length === 0}
            emptyText={t("cmsSeo.noSports", { defaultValue: "No sports match this search." }) as string}
          >
            <DataTable
              rows={visible}
              rowKey={(row) => String(row.id)}
              columns={columns}
              onRowClick={(row) => setFormState({ kind: "edit", path: row.path })}
            />
          </AsyncList>
        </>
      )}

      <RouteSeoModal
        state={formState}
        onClose={() => setFormState({ kind: "none" })}
        onSaved={() => {
          setFormState({ kind: "none" })
          load()
        }}
      />
    </div>
  )
}
